import moment from 'moment'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import GridTable from '../../common/GridTable/GridTable'
import SortButton from '../../common/buttons/SortButton'
import { ReactComponent as OutsideLinkIcon } from '../../../assets/images/common/icons/outside-link-icon.svg'
import ProfileAvatar from '../../common/avatar/ProfileAvatar'
import Loader from '../../common/loader/Loader'

import classes from './StudentsTable.module.scss'
import colors from '../../../assets/styles/globals.scss'

import { GENDER_LABEL_KEYS } from '../../../utility/userHelper'
import { displayWithCurrency } from '../../../utility/currency'
import { getSortStates } from '../../../utility/members'

const FIRST_COLUMN_WIDTH = '320px'

const CELL_VALUES = {
  first_activity: (student) => (student.firstActivity ? moment(student.firstActivity).fromNow(true) : '-'),
  total_enrollments: (student) => student.enrollmentsCount,
  total_revenue: (student, currency) => (student.totalRevenue > 0 ? displayWithCurrency(student.totalRevenue, currency, true) : '-'),
  strokes_lost: (student) => student.strokesLost,
  nine_hole_rounds: (student) => student.nineHoleRoundsCount,
  casual_rounds: (student) => student.casualRoundsCount,
  practice_sessions: (student) => student.practiceSessionsCount,
  time_practiced: (student) => student.timePracticed,
  age: (student) => student.age,
  gender: (student) => student.genderLabel,
  level: (student) => student.currentFormalDivision,
  latest_program_enrolled: (student) => student.latestProgramEnrolled || '-',
}

const renderEnrollmentStatus = (student) => (
  <span
    className={classNames(classes.enrollmentStatus, {
      [classes.enrollmentActive]: student.enrollmentStatus === 'active',
      [classes.enrollmentFuture]: student.enrollmentStatus === 'future',
      [classes.enrollmentWaitlist]: student.enrollmentStatus === 'waitlist',
    })}
  >
    {student.enrollmentStatus}
  </span>
)

const renderCell = (field, student, currency) => (
  field === 'enrollment_status'
    ? renderEnrollmentStatus(student)
    : <span className='font-weight-bold'>{CELL_VALUES[field]?.(student, currency)}</span>
)

const StudentsTable = ({
  students, fetchData, pagination, onStudentClick, inspectedStudentId, isExporting, isLoading, isSortLoading, sortOption, applySort, visibleColumns,
}) => {
  const { t } = useTranslation()
  const hasMore = !pagination?.totalPages || (pagination?.page + 1) <= pagination?.totalPages
  const currency = useSelector(state => state.user.current_user.communities[0].currency)

  if (!students || !pagination) {
    return (
      <div className='h-100 d-flex align-items-center justify-content-center'>
        <Loader>{t('members.loading.students')}</Loader>
      </div>
    )
  }

  const getNextSortValue = (selectedSortField, currentSortValue) => {
    const states = getSortStates(selectedSortField)

    if (selectedSortField !== sortOption.sortField) {
      return states.at(0)
    }

    if (currentSortValue === states.at(-1)) {
      return null
    }

    const currentIndex = states.indexOf(currentSortValue)
    return states[(currentIndex + 1) % states.length]
  }

  const firstColumn = {
    width: FIRST_COLUMN_WIDTH,
    header: t('members.studentsCountHeading', { count: pagination.totalCount }),
    render: (student) => (
      <div className={classNames(classes.studentInfo, 'd-flex align-items-center w-100')} title={`${student.firstName} ${student.lastName}`}>
        <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
        <div className='d-flex flex-column flex-grow-1 pr-3'>
          <h6 className='mb-1 font-weight-bold'>{student.firstName} {student.lastName}</h6>
          <span>{t('members.ageYrsGender', { age: student.age ?? '-', gender: t(GENDER_LABEL_KEYS[student.gender]) })}</span>
        </div>
        <OutsideLinkIcon className={classes.detailsIcon} />
      </div>
    ),
    skeleton: (
      <div className='d-flex align-items-center w-100'>
        <div data-skeleton='avatar' />
        <div className={classNames('d-flex flex-column flex-grow-1 pr-3', classes.skeletonInfoWrapper)}>
          <div data-skeleton='name' />
          <div data-skeleton='info' />
        </div>
      </div>
    ),
  }

  const columns = visibleColumns.map((col) => ({
    key: col.field,
    gridWidth: col.gridWidth,
    align: 'right',
    header: (
      <SortButton
        label={col.label}
        active={sortOption.sortField === col.field && sortOption.sortValue !== null}
        onClick={() => applySort(col.field, getNextSortValue(col.field, sortOption.sortValue))}
        loading={isSortLoading && sortOption.sortField === col.field}
      />
    ),
    render: (student) => renderCell(col.field, student, currency),
    skeleton: <div data-skeleton={col.skeletonType} />,
  }))

  const exportOverlay = isExporting ? (
    <div className={classes.exportLoadingWrapper}>
      <Loader loaderType='ball-beat' loaderColor={colors.lightNavy}>{t('members.loading.exportingMembers')}</Loader>
      <span className='text-center'>{t('members.studentsTable.exportHelp')}</span>
    </div>
  ) : null

  return (
    <GridTable
      id='studentsTable'
      className={classes.table}
      firstColumn={firstColumn}
      columns={columns}
      rows={students}
      rowKey={(student) => student.id}
      hasMore={hasMore}
      onFetchMore={fetchData}
      hideLoader
      onRowClick={onStudentClick}
      selectedRowKey={inspectedStudentId}
      rowClassName={(student) => classNames(classes.studentRow, { [classes.isClicked]: student.id === inspectedStudentId })}
      skeletonRowsCount={hasMore && isLoading ? 3 : 0}
      overlay={exportOverlay}
    >
      {visibleColumns.length === 0 && (
        <div className={classes.noColumnsMessage}>{t('members.studentsTable.noColumnsVisible')}</div>
      )}
    </GridTable>
  )
}

StudentsTable.propTypes = {
  students: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func,
  pagination: PropTypes.object,
  inspectedStudentId: PropTypes.number,
  isExporting: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSortLoading: PropTypes.bool,
  sortOption: PropTypes.object,
  applySort: PropTypes.func,
  visibleColumns: PropTypes.array.isRequired,
}

export default StudentsTable
